import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '10px',
    marginTop: 0,
    marginBottom: 0,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  listItem: {
    paddingLeft: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  overview: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(4),
  },
}));

const PrivacyPolicy = ({ hideHeading = false }) => {
  const classes = useStyles();

  return (
    <Container maxWidth="md">
      <Paper className={classes.root}>
        {!hideHeading && (
          <>
            <Typography variant="h4" gutterBottom align="center">
              Privacy Policy – NimblePark
            </Typography>
            <Typography variant="subtitle1" gutterBottom align="center">
              Effective Date: April 17, 2025
            </Typography>
            <Typography variant="subtitle2" gutterBottom align="center">
              Applies To: NimblePark mobile app, website, and all users globally
            </Typography>
          </>
        )}

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Introduction
          </Typography>
          <Typography paragraph>
            NimblePark is a valet and parking management platform that helps users find, reserve, and manage parking spaces and valet services across cities. We're committed to protecting your data and respecting your privacy.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 1 – What We Collect & Why
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            We gather personal details such as:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Name, address, email, and phone number" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Vehicle details (number plate, brand, model)" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Location data (live or background)" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Technical data like IP address, device type, OS version" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="App usage (click patterns, session logs)" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Payment metadata (processed securely by third-party services)" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Purpose of Collection:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Deliver efficient customer support" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Enable smooth valet pick-up/drop" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Process transactions securely" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Send receipts, service updates, and offers (with permission)" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Provide driving and parking analytics" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Tailor services based on user preferences" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Suggest nearby parking zones and show real-time vehicle status" />
            </ListItem>
          </List>
        </Box>

        <Divider className={classes.divider} />

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 2 – Consent
          </Typography>
          <Typography paragraph>
            By submitting your details, you grant NimblePark permission to collect and use it for the stated purposes.
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="For additional uses like marketing or communication, explicit opt-in is required" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="You may alter consent preferences anytime via app settings" />
            </ListItem>
          </List>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 3 – Disclosure
          </Typography>
          <Typography paragraph>
            We do not sell your data. Data may be shared only:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="When legally mandated (court orders, law enforcement, etc.)" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Upon breach of policies or to protect platform integrity" />
            </ListItem>
          </List>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 4 – External Services and Links
          </Typography>
          <Typography paragraph>
            We integrate with trusted third-party services like:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Google Maps (navigation)" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Razorpay, Paytm, Stripe, Gpay (payments)" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Firebase (analytics)" />
            </ListItem>
          </List>
          <Typography paragraph>
            Each has its own privacy practices. When redirected externally, their terms apply.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 5 – Payment Security
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="NimblePark does not store your card details" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Payment metadata is stored temporarily (for receipts and audits)" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="All transactions are processed via PCI-DSS-compliant gateways" />
            </ListItem>
          </List>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 6 – Third-Party Access
          </Typography>
          <Typography paragraph>
            Service coordination may require limited third-party data access. Examples include:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Property owners" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Valet staff" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Managers" />
            </ListItem>
          </List>
          <Typography paragraph>
            They are granted access only for operations and are expected to maintain confidentiality. NimblePark is not liable for external misuse or misconduct.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 7 – Security Commitment
          </Typography>
          <Typography paragraph>
            We implement industry-standard safeguards including:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="SSL encryption" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Role-based access control" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Encrypted backups" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="ISO-compliant data deletion after expiry" />
            </ListItem>
          </List>
          <Typography paragraph>
            Users are advised to maintain device-level security for optimal protection.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 8 – Cookies (Web Only)
          </Typography>
          <Typography paragraph>
            We use session cookies to:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Enhance UI responsiveness" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Remember login sessions" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Analyze on-page behavior" />
            </ListItem>
          </List>
          <Typography paragraph>
            These are not used to track users across platforms. You can manage cookies via browser settings.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 9 – Age Policy
          </Typography>
          <Typography paragraph>
            By using NimblePark, you confirm that you are of legal age in your jurisdiction or using the app under guardian supervision.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 10 – Policy Updates
          </Typography>
          <Typography paragraph>
            We may update this Privacy Policy. Any major revisions will be posted here. We recommend users check this page periodically for changes.
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section 11 – Lending Services (if applicable)
          </Typography>
          <Typography paragraph>
            If NimblePark enables lending-related services, we ensure:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Data is retained only as legally required" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Permissions (camera/location/mic) are accessed once with consent" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="We do not access call logs, contact lists, or SMS unless explicitly permitted" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="User details may be shared only with authorized property or service personnel, strictly for service coordination" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="All data is stored securely" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Deleting your account results in permanent data removal unless required for legal/regulatory retention" />
            </ListItem>
          </List>
        </Box>

        <Box className={classes.overview}>
          <Typography variant="h6" gutterBottom>
            📱 In-App Privacy Overview (Short Version)
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom>
            Why We Collect Data:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Fast valet/parking booking" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Track your vehicle in real-time" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Get accurate location-based suggestions" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Receive receipts and customer support" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" gutterBottom>
            What We Store:
          </Typography>
          <List>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Name, phone number" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Vehicle & booking history" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Location (while in use/background — with consent)" />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemText primary="Payments (via secure gateways only)" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" gutterBottom>
            Account Deletion:
          </Typography>
          <Typography paragraph>
            Use the "Delete Account" option in app settings. Data deletion is permanent. Both customers and property owners can delete their accounts at any time using the 'Delete Account' option in the app settings. Please note that account deletion is permanent and all associated data will be erased as per guidelines.
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            Consent Management:
          </Typography>
          <Typography paragraph>
            You can opt out of SMS, email notifications, and marketing updates anytime via app settings.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy; 